// The pause menu's pure half (task 12b): what the player list SAYS.
//
// No DOM, no three, no fetch — a projection of `net/presence.ts`'s store plus
// the local player's feet, so the whole thing is headless-testable. The DOM half
// is `ui/pause-menu.ts`.
//
// COORDINATES ARE FEET, everywhere (see shared/protocol.ts). The readout shows
// the block a player is standing on, not their eyes.

import { CHUNK_SIZE, VIEW_DISTANCE_CHUNKS } from '../../shared/constants.js'
import type { Avatar, Vec3 } from '../net/presence.js'

/** One row of the "Jucători" list. */
export interface PlayerRow {
  userId: number
  name: string
  color: string
  /** Rounded FEET position — what the readout shows. */
  x: number
  y: number
  z: number
  /** Ground-plane (x/z) distance from you, in blocks, rounded. */
  distance: number
  /** Beyond view distance you cannot possibly see them — the list says so. */
  outOfSight: boolean
  /** True for the local player's own row (rendered as "(tu)", never a target). */
  isSelf: boolean
}

/** How far the client renders: 6 chunks × 16 = 96 blocks. Past it, fog. */
export const VIEW_DISTANCE_BLOCKS = VIEW_DISTANCE_CHUNKS * CHUNK_SIZE

/**
 * `mama@example.com` → `mama`. The same rule the server applies when it builds
 * `PlayerState.name` (`server/world/room.ts` `displayName`) — duplicated rather
 * than imported, because a client module must never reach into `src/server/`.
 * The local player is not in the presence store, so this is the only place the
 * client can learn its own display name.
 */
export function displayName(email: string): string {
  const at = email.indexOf('@')
  return at > 0 ? email.slice(0, at) : email
}

/** Ground-plane distance: a player 100 blocks below you is still "here". */
export const groundDistance = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.z - b.z)

/** `-146.4` → `-146`. Feet land on integers; the fraction is sub-block jitter. */
const round = (n: number): number => Math.round(n)

/** "x -146  y 32  z 313" — the readout the two-browser session badly missed. */
export function formatCoords(p: Vec3): string {
  return `x ${round(p.x)}  y ${round(p.y)}  z ${round(p.z)}`
}

/** "349 blocuri" / "12 blocuri" / "0 blocuri" — Romanian, no pluralization trap. */
export function formatDistance(blocks: number): string {
  return `${round(blocks)} ${round(blocks) === 1 ? 'bloc' : 'blocuri'}`
}

/**
 * The list: YOU first (always, so the coordinate readout has a home), then every
 * remote player ordered by `userId` — a STABLE key that does not change while a
 * player walks.
 *
 * It used to sort nearest-first (`a.distance - b.distance`), recomputed on every
 * 250 ms refresh tick. With float positions the tiebreak never fires, so two
 * players at similar range swapped places under the cursor four times a second
 * and the row you were aiming at slid away: half of the "I find it hard to click
 * on the users in the menu" bug (the other half was the DOM rebuild, see
 * `pause-menu.ts`). Distance is still computed and still shown — as TEXT, which
 * can update without moving anything.
 *
 * `avatars` comes straight from `PresenceStore.list()` — which already excludes
 * self — so nobody can appear twice.
 */
export function playerRows(
  self: { userId: number; name: string; color: string; position: Vec3 },
  avatars: readonly Avatar[]
): PlayerRow[] {
  const rows: PlayerRow[] = avatars
    .filter(a => a.userId !== self.userId)
    .map(a => {
      const distance = groundDistance(self.position, a.target)
      return {
        userId: a.userId,
        name: a.name,
        color: a.color,
        x: round(a.target.x),
        y: round(a.target.y),
        z: round(a.target.z),
        distance: round(distance),
        outOfSight: distance > VIEW_DISTANCE_BLOCKS,
        isSelf: false
      }
    })
    .sort((a, b) => a.userId - b.userId)

  const me: PlayerRow = {
    userId: self.userId,
    name: self.name,
    color: self.color,
    x: round(self.position.x),
    y: round(self.position.y),
    z: round(self.position.z),
    distance: 0,
    outOfSight: false,
    isSelf: true
  }
  return [me, ...rows]
}

/** May the "Teleportează-mă" button act on this row? Never on yourself. */
export const canTeleportTo = (row: PlayerRow | null | undefined): row is PlayerRow =>
  !!row && !row.isSelf

/** The list is empty of OTHERS — the menu says so instead of showing a void. */
export const isAloneInWorld = (rows: readonly PlayerRow[]): boolean =>
  rows.filter(r => !r.isSelf).length === 0
